// offline-sync.ts — drains the backlit.offline outbox from the page.
//
// A pass:
//   0. sweeps the outbox (sweepExpired): ops quarantined 30 d (revoked: 7 d)
//      or held 7 d are dropped, with `discarded` (a held op also `failed`);
//   1. asks the edge whose session this is: one heartbeat (joining a lifecycle
//      beat already on the wire), bounded at 10 s (no answer by then is no
//      2xx). No 2xx, no pass — and a 401 holds the queue until a later 2xx
//      beat. The drain uid is the beat's X-Backlit-UID or,
//      from an older edge that states none, the record's uid while this page
//      holds its own proof of it (SECR-3). Only that uid's ops are sent —
//      interleaved in queue order with the capture ops (CAPTURE_UID), which
//      carry no identity and drain in every pass, even one with no drain uid. A
//      beat that itself named the uid also releases that uid's quarantined ops
//      (never revoked ones) back into the queue — a page proof never does;
//   2. takes the cross-tab drain lock (Web Locks, ifAvailable — a tab already
//      draining means this one doesn't). Without Web Locks each op is claimed
//      under a 30 s outbox lease instead, never renewed: a duplicate send is
//      absorbed by the CAS and the witness==ours rule (SYNC-2);
//   3. replays the ops strictly in queue order, one request at a time
//      (transport.ts replayWrite, with X-Backlit-Expect-UID; a capture create
//      goes to create-at under its device-minted handle — never the legacy
//      ?prefix= route, which would mint another — and an update by handle,
//      neither with Expect-UID), applying the outcome table (handle) until the
//      queue is empty or an outcome stops it.
//
// Outcomes: a 2xx commits (mirror write-through behind the uid gate, op
// removed, `synced` — the fold carries the replay's interval, from just before
// the request to the commit, and reconciles against the stored evidence like
// every mirror fold, so an entry read after the replay began and holding
// another crc becomes "unknown" rather than being overwritten); a 412 witness equal to ours (puts only; records: owned
// by the op's uid) is an earlier attempt that landed — a commit too; another
// witness, or not_found on a based put, is a conflict for the pipeline in
// offline-conflict.ts (rebase, onConflict, else remote wins — its commits
// count as conflicts, not sent); a DELETE 404 (not glow_not_found) is done;
// an unmarked DELETE 412 (a lost race) is retried; 401 holds the queue; 409
// identity_mismatch quarantines the uid's ops; a 429 — and the long pauses, a
// 503 glow_dark, a 404 glow_not_found and any 421 — pause the drain 15 min
// (pauseUntil, posted to the other tabs as a `pause` message, which they
// adopt); 413 quota_exceeded holds the op (retried every 15 min until the
// sweep drops it), and so does a capture create-at an older data-api answers
// 404 / 405; a capture update's unknown_handle backs off while a create of
// that handle is still in the outbox, else drops; another 4xx drops the op
// with `failed`; anything else (network, 5xx, other 404s) backs off until
// retryAt. Capture ops never touch the mirror, never reach the conflict
// pipeline and are never quarantined.
//
// The retry timer: a visible tab with work left — sendable ops, or held ones
// (due at their leaseUntil) — arms one timer for max(retryAt, pauseUntil,
// the last pass's start + 60 s × 2ⁿ (capped at 30 min, ±25 %)), n counting
// the timer's firings since a pass last sent anything; so a server error
// while online still retries, at most ~2 per hour once saturated. Going
// hidden clears it, becoming visible re-arms it; the drain lock keeps it to
// one drainer. The online transition (noteOnline false → true) clears retryAt
// only — never backoffN or pauseUntil. Every non-terminal outcome is
// written back with Outbox.patch, which never touches an op that is gone or
// was quarantined meanwhile (quarantine is sticky). A pass whose every
// attempted op reached a terminal outcome and that emptied the uid's queue
// stamps lastSyncedAt (kept in the mirror's meta, so a purge resets it).
//
// Lifecycle: stopDrain (disable(), clear(), logout) claims no further op and
// sends no further conflict-round request; the request on the wire finishes
// and its outcome is kept (a stopped drain only invalidates, never writes, the
// mirror entry). resumeDrain lifts it. Every mirror write here (a commit's
// fold or invalidate, a drop's invalidate) is skipped when the purge epoch
// (ctx.epoch(), offline.ts Rule D) moved since its request started — a purge
// meanwhile leaves the mirror alone, while the outbox outcome still applies.
//
// Triggers: enable(), window `online`, the page becoming visible, a 2xx beat
// ending a 401 hold, a 2xx response while the signed-in uid has queued ops
// (not during a 401 hold, and not within 30 s of the last pass — so a busy
// page costs at most one heartbeat probe per 30 s), a newly queued write, the
// retry timer, sync() — which alone ignores retryAt — and the reachability
// probe (offline-probe.ts) getting its answer while offline, which hands its
// beat over (requestDrain's beat) so the pass sends no second heartbeat. Every
// one waits out a pause; one arriving during a pass runs a single follow-up
// pass (a handed beat carries over to it). Another
// tab's `outbox` poke starts no pass (the tab that queued kicked its own): it
// only restates the state; its `pause` sets this tab's pauseUntil too.
//
// State: a run emits `state` (ctx.emitState, the one coalescing writer) when
// it starts and again once it has cleared its running flag, so the last state
// event after a run always reads syncing: false.

import type { RecordResult } from "./backend.js";
import type { BacklitError, BacklitErrorCode } from "./errors.js";
import { backoffDelay } from "./events.js";
import { onHeartbeatResult, sendHeartbeatWithUid } from "./heartbeat.js";
import { emitLocalChange, hasPending, invalidatePendingHint, opName, type OfflineContext } from "./offline-backend.js";
import { DRAIN_LOCK_NAME } from "./offline-channel.js";
import { forgetRebase, initConflictChannel, runConflictPipeline } from "./offline-conflict.js";
import { now, partitionFor, writeThroughAllowed, type MirrorStoreName } from "./offline-identity.js";
import { CAPTURE_UID, HELD_TTL_MS, META_SYNCED, pendingCounts, type OutboxOp } from "./offline-store.js";
import type { OfflineSyncResult } from "./offline.js";
import { SERVER_CODES, type ServerErrorCode } from "./server-codes.gen.js";
import { addResponseObserver, captureCreateAt, remoteBackend, replayWrite, type ReplayResult } from "./transport.js";

export const LEASE_MS = 30_000;
// PAUSE_MS is both the 429 / long pause and a held op's retry interval.
export const PAUSE_MS = 900_000;
export const HELD_MAX_MS = HELD_TTL_MS;
// The retry timer's growth: RETRY_BASE_MS × 2ⁿ, capped at RETRY_MAX_MS, ±25 %.
export const RETRY_BASE_MS = 60_000;
export const RETRY_MAX_MS = 1_800_000;

// QuarantineReason is why quarantineUid held a uid's ops back (it is also the
// ops' lastError): "revoked" ops expire after 7 d and are never released.
export type QuarantineReason = "logout" | "signed_out" | "identity_change" | "identity_mismatch" | "revoked" | "session_ended";

// --- the conflict pipeline's view of the drain (offline-conflict.ts) -------------

// Replayed is a replay's answer plus t0, when its request started (the start
// of the evidence interval its mirror fold carries), and ep, the purge epoch
// then (its fold is skipped once the epoch has moved).
export type Replayed = ReplayResult & { t0: number; ep: number };

// Conflict is a queued write the server refused as out of date: remote is the
// witness (null = the key is absent), uid the replay response's X-Backlit-UID,
// t0 the start of the request that answered remote and ep the purge epoch then.
export interface Conflict {
  op: OutboxOp;
  remote: RecordResult | null;
  uid: string;
  t0: number;
  ep: number;
}

export interface ConflictIO {
  // cas replays op's key as a CAS against expected (null bytes: a conditional
  // DELETE).
  cas(op: OutboxOp, bytes: ArrayBuffer | null, contentType: string, expected: string): Promise<Replayed>;
  // settle applies the outcome table to a non-412 answer; a 2xx (or a DELETE
  // 404) commits bytes, removes group, emits conflict{resolution} + the
  // corrective change.
  settle(
    op: OutboxOp,
    r: Replayed,
    bytes: ArrayBuffer | null,
    contentType: string,
    group: OutboxOp[],
    resolution: "local" | "merged",
  ): Promise<boolean>;
  // remoteWins drops group and adopts the remote value.
  remoteWins(c: Conflict, group: OutboxOp[]): Promise<void>;
}

// --- state ------------------------------------------------------------------------

type Counts = { sent: number; conflicts: number; failed: number };
type Session = { status: number; uid: string };
const zero = (): Counts => ({ sent: 0, conflicts: 0, failed: 0 });
const ok = (s: number) => s >= 200 && s < 300;

let ctx: OfflineContext | null = null;
let stopped = true;
let running: Promise<OfflineSyncResult> | null = null;
let again = false;
// againBeat: a session answer handed to a request that joined a running pass,
// used by the follow-up pass instead of a heartbeat of its own.
let againBeat: Session | undefined;
let held401 = false;
let pauseUntil = 0;
let retryAt = 0;
let quietUntil = 0;
let backoffN = 0;
// lastPassAt: when the last pass started; timerN: the retry timer's firings
// since a pass last sent anything; timer: the armed retry timer; armSeq
// voids an arming still reading the outbox.
let lastPassAt = 0;
let timerN = 0;
let timer: unknown = null;
let armSeq = 0;
let arming: Promise<void> = Promise.resolve();
type TimerFns = { set: (fn: () => void, ms: number) => unknown; clear: (t: unknown) => void; rand: () => number };
const realTimers: TimerFns = {
  set: (fn, ms) => setTimeout(fn, ms),
  clear: (t) => clearTimeout(t as ReturnType<typeof setTimeout>),
  rand: Math.random,
};
let timers = realTimers;
let counts = zero();
let synced = 0;
// clean: every op the current pass attempted reached a terminal outcome.
let clean = true;
let offs: Array<() => void> = [];
let probe: () => Promise<Session> = sendHeartbeatWithUid;

// getSyncContext is the runtime the drain runs against (the conflict pipeline
// reads the outbox, options().onConflict, channel() and tabId through it).
export const getSyncContext = (): OfflineContext | null => ctx;
export const isSyncing = (): boolean => running !== null;
// isHeldForSignIn: a 401 holds the queue until a 2xx beat (the next sign-in).
export const isHeldForSignIn = (): boolean => held401;
// getLastSyncedAt is when a clean pass last emptied the queue (0 = never);
// setLastSyncedAt adopts the value persisted in the mirror (0 after a purge).
export const getLastSyncedAt = (): number => synced;
export const setLastSyncedAt = (t: number): void => void (synced = t);

// bindSync points the drain at c without starting it (enable() binds it
// before it applies identity signals and sweeps).
export const bindSync = (c: OfflineContext): void => void (ctx = c);

// within resolves once p settles or after ms, whichever comes first.
export function within(p: Promise<unknown>, ms: number): Promise<void> {
  let t: ReturnType<typeof setTimeout> | undefined;
  const done = () => clearTimeout(t);
  return Promise.race([p, new Promise((r) => (t = setTimeout(r, ms)))]).then(done, done);
}

// withinValue resolves p's value, or fallback after ms (a rejection of p
// passes through); its timer is cleared once p settles, and unref'd.
export function withinValue<T>(p: Promise<T>, ms: number, fallback: T): Promise<T> {
  let t: ReturnType<typeof setTimeout> | undefined;
  const late = new Promise<T>((r) => {
    t = setTimeout(() => r(fallback), ms);
    (t as { unref?: () => void }).unref?.();
  });
  const done = () => clearTimeout(t);
  p.then(done, done);
  return Promise.race([p, late]);
}

// quarantineUid holds uid's unsent ops back (never capture's) for reason and,
// when any changed, tells watchers (quarantined, then state) and pokes the
// other tabs. Store failures reject. It runs inside serial tasks, so the state
// emission is fire-and-forget (awaiting it there would deadlock the queue).
export async function quarantineUid(uid: string, reason: QuarantineReason): Promise<void> {
  const c = ctx;
  const count = c ? await c.outbox.quarantineUid(uid, now(), { revoked: reason === "revoked", reason }) : 0;
  if (!c || !count) return;
  c.emit({ type: "quarantined", count, reason });
  c.channel()?.post({ type: "outbox" });
  c.emitState();
}

// sweepExpired drops the ops past their TTL (Outbox.sweepExpired): a held op
// fails like any dropped op (failed, the mirror entry invalidated, a
// corrective change); each non-empty bucket emits one discarded. Never
// rejects.
export async function sweepExpired(): Promise<void> {
  const c = ctx;
  try {
    const r = await c!.outbox.sweepExpired(now());
    for (const o of r.held) await drop(o, SERVER_CODES.has(o.lastError as ServerErrorCode) ? o.lastError : "internal");
    for (const [k, why] of [
      ["quarantine", "quarantine_expired"],
      ["revoked", "revoked_expired"],
      ["held", "held_expired"],
    ] as const) {
      if (!r[k][0]) continue;
      for (const o of r[k]) forgetRebase(o.opId);
      invalidatePendingHint();
      c!.emit({ type: "discarded", count: r[k].length, reason: why });
    }
  } catch {
    /* the next sweep retries */
  }
}

// stopDrain stops the drain: no op is claimed, and no conflict-round request
// sent, after it — the request on the wire finishes and its outcome is kept.
// It resolves once the drain is idle, or after waitMs. resumeDrain lifts it.
export function stopDrain(waitMs: number): Promise<void> {
  stopped = true;
  disarm();
  return within(running ?? Promise.resolve(), waitMs);
}

export const resumeDrain = (): void => void (stopped = false);

// startSync starts draining for c (enable()): it installs the triggers and
// the rebase-request responder, and asks for a pass.
export function startSync(c: OfflineContext): void {
  ctx = c;
  stopped = false;
  const kick = () => void requestDrain();
  if (offs.length === 0) {
    const listen = (t: EventTarget | undefined, type: string, fn: () => void) => {
      if (typeof t?.addEventListener !== "function") return;
      t.addEventListener(type, fn);
      offs.push(() => t.removeEventListener(type, fn));
    };
    const d = typeof document !== "undefined" ? document : undefined;
    listen(typeof window !== "undefined" ? window : undefined, "online", kick);
    listen(d, "visibilitychange", () => (d?.hidden ? disarm() : kick()));
    offs.push(
      onHeartbeatResult((s) => {
        if (held401 && !running && ok(s)) {
          held401 = false;
          kick();
        }
      }),
      addResponseObserver((m) => {
        if (ok(m.status) && !running && !held401 && now() >= quietUntil) {
          void hasPending(c.identity()?.uid ?? "").then((y) => y && kick(), () => {});
        }
      }),
    );
    const ch = c.channel();
    if (ch) offs.push(ch.on((m) => (m.type === "outbox" ? c.emitState() : m.type === "pause" && adoptPause(m.until))), initConflictChannel());
  }
  kick();
}

// stopSync removes the triggers (disable()). A request on the wire finishes;
// the pass stops before the next op.
export function stopSync(): void {
  stopped = true;
  disarm();
  for (const f of offs.splice(0)) f();
}

// adoptPause takes another tab's pause (never past PAUSE_MS from now) and
// re-arms the retry timer.
function adoptPause(until: number): void {
  pauseUntil = Math.max(pauseUntil, Math.min(until, now() + PAUSE_MS));
  armRetry();
}

// clearRetryAt is the online transition: it clears retryAt only (never
// backoffN or pauseUntil) and re-arms the retry timer.
export function clearRetryAt(): void {
  retryAt = 0;
  armRetry();
}

// retryGrowth is the retry timer's own growth for n firings: RETRY_BASE_MS ×
// 2ⁿ capped at RETRY_MAX_MS, with ±25 % jitter (not backoffDelay, which caps
// at 30 s).
export function retryGrowth(n: number, rand: () => number = Math.random): number {
  const base = Math.min(RETRY_MAX_MS, RETRY_BASE_MS * 2 ** Math.min(n, 16));
  return base + base * (rand() * 0.5 - 0.25);
}

const visible = (): boolean => typeof document === "undefined" || document.hidden !== true;

// disarm clears the retry timer (and voids an arming under way).
function disarm(): void {
  armSeq++;
  if (timer !== null) timers.clear(timer);
  timer = null;
}

// dueAt is when the drain next has work in ops for uid: 0 when an op is
// sendable now, the earliest held op's leaseUntil when only held ones are
// left, or -1 for none.
function dueAt(ops: readonly OutboxOp[], uid: string): number {
  const [p, , h] = pendingCounts(ops, uid);
  if (p > h) return 0;
  let at = -1;
  for (const o of ops) {
    if (o.state === "held" && ((uid !== "" && o.uid === uid) || o.uid === CAPTURE_UID) && (at < 0 || o.leaseUntil < at)) at = o.leaseUntil;
  }
  return at;
}

// armRetry (re)arms the one retry timer when this tab is visible, draining,
// not held for sign-in, and has work left (dueAt).
function armRetry(): void {
  disarm();
  const c = ctx;
  if (!c || stopped || held401 || !visible()) return;
  const seq = armSeq;
  arming = c.outbox.all().then(
    (ops) => {
      if (seq !== armSeq || ctx !== c || stopped || held401 || !visible()) return;
      const due = dueAt(ops, c.identity()?.uid ?? "");
      if (due < 0) return;
      const at = Math.max(retryAt, pauseUntil, due, lastPassAt + retryGrowth(timerN, timers.rand));
      timer = timers.set(
        () => {
          timer = null;
          timerN++;
          void requestDrain();
        },
        Math.max(0, at - now()),
      );
      (timer as { unref?: () => void } | null)?.unref?.();
    },
    () => {},
  );
}

// requestDrain asks for a pass and resolves its counts plus the signed-in
// uid's ops left. explicit (sync()) ignores a backoff's retryAt; nothing
// ignores a 429 pause. A request during a pass joins it (one follow-up pass).
// beat is a session answer the caller already holds (the reachability probe):
// the pass uses it instead of sending a heartbeat, and never waits for one.
export function requestDrain(explicit = false, beat?: Session): Promise<OfflineSyncResult> {
  const t = now();
  if (!ctx || stopped || t < pauseUntil || (!explicit && t < retryAt)) {
    armRetry();
    return result(zero());
  }
  if (running) {
    again = true;
    if (beat) againBeat = beat;
    return running;
  }
  const c = ctx;
  const n = (counts = zero());
  let p!: Promise<OfflineSyncResult>;
  p = running = (async () => {
    try {
      let hb = beat;
      do {
        again = false;
        await pass(hb);
        hb = againBeat;
        againBeat = undefined;
      } while (again && !stopped && !held401 && now() >= Math.max(pauseUntil, retryAt));
    } finally {
      if (running === p) running = null;
      againBeat = undefined;
      if (n.sent + n.conflicts + n.failed > 0) timerN = 0;
      armRetry();
      // After the flag clears: the last state event reads syncing: false.
      c.emitState();
    }
    return result(n);
  })();
  c.emitState();
  return p;
}

// result is a pass's counts plus pending as status() counts it (the signed-in
// uid's and capture's ops, quarantined ones excluded).
async function result(n: Counts): Promise<OfflineSyncResult> {
  let pending = 0;
  try {
    if (ctx) pending = pendingCounts(await ctx.outbox.all(), ctx.identity()?.uid ?? "")[0];
  } catch {
    /* report 0 */
  }
  return { ...n, pending };
}

// BEAT_MS bounds a pass's heartbeat: no answer by then is status 0, no pass.
const BEAT_MS = 10_000;
let beatMs = BEAT_MS;

// pass runs one drain pass; beat, when given, is its session answer (no
// heartbeat of its own).
async function pass(beat?: Session): Promise<void> {
  lastPassAt = now();
  quietUntil = lastPassAt + LEASE_MS;
  try {
    const c = ctx!;
    // The sweep runs alongside the beat (it needs no session).
    const [, hb] = await Promise.all([sweepExpired(), beat ?? withinValue(probe(), beatMs, { status: 0, uid: "" })]);
    if (hb.status === 401) held401 = true;
    if (!ok(hb.status)) return;
    held401 = false;
    const id = c.identity();
    // No drain uid: the pass sends capture ops only.
    const uid = hb.uid || (id && c.hasPageProof() ? id.uid : "");
    if (hb.uid) await c.outbox.releaseQuarantine(uid);
    const locks = (globalThis as { navigator?: { locks?: LockManager } }).navigator?.locks;
    if (locks?.request) {
      await locks.request(DRAIN_LOCK_NAME, { ifAvailable: true }, async (l) => {
        if (l) await loop(uid, true);
      });
    } else {
      await loop(uid, false);
    }
  } catch {
    /* a store failure ends the pass; the next trigger retries */
  }
}

async function loop(uid: string, force: boolean): Promise<void> {
  clean = true;
  while (!stopped) {
    const op = await ctx!.outbox.claimHead(uid ? [uid, CAPTURE_UID] : [CAPTURE_UID], now(), LEASE_MS, force);
    if (op === "busy") return;
    if (!op) {
      // A capture-only pass empties no signed-in uid's queue.
      if (clean && !stopped && (uid || !ctx!.identity()?.uid)) await ctx!.mirror.setMeta(META_SYNCED, (synced = now())).catch(() => {});
      return;
    }
    if (!(await handle(op, await send(op, op.bytes, op.contentType, op.base)))) return;
  }
}

// NET is the answer to a request never made (a stopped drain) or that failed
// in transport.
const NET: ReplayResult = { status: 0, code: "network", crc32c: "", owner: "", uid: "", witnessMarked: false, witness: null };

// send replays op's key; once the drain is stopped it sends nothing and
// answers as a transport failure (the op goes back to the queue). A capture
// op's thrown failure is read into the same answer (status + code). The
// answer carries t0 and the purge epoch ep, both taken just before the request.
function send(op: OutboxOp, bytes: ArrayBuffer | null, contentType: string, base: string | null): Promise<Replayed> {
  const t0 = now();
  const ep = ctx!.epoch();
  const r: Promise<ReplayResult> = stopped
    ? Promise.resolve({ ...NET })
    : op.uid === CAPTURE_UID
      ? (op.op === "captureCreate" ? captureCreateAt : remoteBackend.captureUpdate)(op.key, bytes as ArrayBuffer, contentType).then(
          () => ({ ...NET, status: 200, code: "" }),
          (e: BacklitError) => ({ ...NET, status: e.status | 0, code: e.code || "network" }),
        )
      : replayWrite({ store: op.store as MirrorStoreName, key: op.key, bytes, contentType, base, expectUid: op.uid });
  return r.then((x) => ({ ...x, t0, ep }));
}

// handle applies the outcome table to op's replay answer r; it resolves
// whether the pass goes on to the next op.
async function handle(op: OutboxOp, r: Replayed): Promise<boolean> {
  const c = ctx!;
  const s = r.status;
  const code = r.code;
  const t = now();
  const put = op.op === "put";
  const w = r.witness;
  const conflict = (remote: RecordResult | null) => runConflictPipeline({ op, remote, uid: r.uid, t0: r.t0, ep: r.ep }, io);
  if (ok(s)) return done(op, r.crc32c, r.owner, r.uid, r);
  // (Only a capture update answers unknown_handle.) While a create of that
  // handle is still in the outbox (queued or held) the update backs off below;
  // otherwise it is dropped.
  if (code === "unknown_handle" && !(await c.outbox.forKey(CAPTURE_UID, "capture", op.key)).some((o) => o.op === "captureCreate")) {
    await drop(op, code);
    return true;
  }
  if (s === 412 && w) {
    return put && w.crc32c === op.crc32c && (op.store !== "records" || w.owner === op.uid)
      ? done(op, w.crc32c, w.owner, r.uid, r)
      : conflict(w);
  }
  if (s === 404 && op.op === "delete" && code !== "glow_not_found") return done(op, "", "", r.uid, r);
  if (s === 404 && code === "not_found" && op.base) return conflict(null);
  if (s === 401) {
    held401 = true;
    await c.outbox.patch(op.seq, { state: "queued" });
    return false;
  }
  if (s === 409 && code === "identity_mismatch") {
    await quarantineUid(op.uid, "identity_mismatch");
    return false;
  }
  // A 413 quota_exceeded — or a capture create-at an older data-api answers
  // 404 / 405 (not glow_not_found) — is held (due again after PAUSE_MS; the
  // sweep drops it once held HELD_TTL_MS); other 4xx (not 404 / 412 / 421 /
  // 429) drop.
  const q =
    (s === 413 && code === "quota_exceeded") || (op.op === "captureCreate" && (s === 405 || (s === 404 && code !== "glow_not_found")));
  if (!q && s > 399 && s < 500 && ![404, 412, 421, 429].includes(s)) {
    await drop(op, code || "internal");
    return true;
  }
  // Hold, or requeue: a 429 or a long pause (Dark, not found, misdirected)
  // pauses the whole drain — and every tab's — everything else backs off.
  clean = false;
  await c.outbox.patch(op.seq, {
    state: q ? "held" : "queued",
    heldSince: q ? op.heldSince || t : op.heldSince,
    leaseUntil: q ? t + PAUSE_MS : 0,
    attempts: op.attempts + 1,
    lastError: code || String(s),
  });
  if (s === 429 || s === 421 || (s === 503 && code === "glow_dark") || (s === 404 && code === "glow_not_found")) {
    pauseUntil = Math.max(pauseUntil, t + PAUSE_MS);
    c.channel()?.post({ type: "pause", until: pauseUntil });
  } else if (!q) retryAt = t + backoffDelay(backoffN++);
  return q;
}

// part is the mirror partition op's key lives in (its uid's), or null — always
// for a capture op (never mirrored), so a commit or drop of one touches only
// the outbox and fires no storage.change.
function part(op: OutboxOp): string | null {
  const id = ctx!.identity();
  return id && op.uid !== CAPTURE_UID ? partitionFor(op.store as MirrorStoreName, { ...id, uid: op.uid }) : null;
}

// forget removes group from the outbox (and its rebase closures).
async function forget(group: OutboxOp[]): Promise<void> {
  await ctx!.outbox.remove(group.map((o) => o.seq));
  for (const o of group) forgetRebase(o.opId);
  invalidatePendingHint();
}

// commit records that the server now holds bytes (null: absent) at op's key,
// as evidence over [at.t0 (the answering request's start), now] — only while
// the purge epoch is still at.ep, else the mirror is left alone: the mirror
// folds it (reconcile) — a present entry or a tombstone — only when the
// response's own uid passes the write-through gate. Otherwise (a put without a
// crc, a stopped drain, a response with no uid from an older server, or a
// failed mirror write) the key is invalidated to "unknown" — never deleted,
// which under a complete listing would read as absent — except when the
// response names ANOTHER user: that write landed in their silo, whose
// partition the identity change purges, so this one is left untouched. group
// leaves the outbox and the other tabs are poked.
async function commit(
  op: OutboxOp,
  crc: string,
  bytes: ArrayBuffer | null,
  ct: string,
  owner: string,
  uid: string,
  group: OutboxOp[],
  at: { t0: number; ep: number },
): Promise<void> {
  const c = ctx!;
  const s = op.store as MirrorStoreName;
  const p = part(op);
  const t0 = at.t0;
  const fresh = () => c.epoch() === at.ep;
  try {
    if (p && (uid === "" || writeThroughAllowed(p, uid)) && fresh()) {
      const t1 = now();
      const put =
        !stopped &&
        writeThroughAllowed(p, uid) &&
        (!bytes || crc !== "") &&
        (await c.mirror
          .put({
            s,
            p,
            k: op.key,
            state: bytes ? "present" : "absent",
            value: bytes,
            contentType: bytes ? ct : "",
            crc32c: crc,
            size: bytes ? bytes.byteLength : 0,
            ...(s === "records" && bytes ? { owner: owner || op.uid } : {}),
            fetchedAt: t1,
            accessedAt: 0,
            seenFrom: t0,
            seenTo: t1,
          })
          .catch(() => "failed" as const)) !== "failed";
      if (!put && fresh()) await c.mirror.invalidate(s, p, op.key);
    }
  } catch {
    /* the mirror is a cache: an invalidate that failed too is swallowed */
  }
  await forget(group);
  c.channel()?.post({ type: "outbox" });
}

async function done(op: OutboxOp, crc: string, owner: string, uid: string, at: { t0: number; ep: number }): Promise<boolean> {
  await commit(op, crc, op.bytes, op.contentType, owner, uid, [op], at);
  backoffN = retryAt = 0;
  counts.sent++;
  ctx!.emit({ type: "synced", store: op.store, key: op.key, operation: opName(op) });
  return true;
}

// change emits op's corrective storage.change (no pending tag): a set of crc
// (records: with the owner), or — crc null — a delete.
function change(op: OutboxOp, crc: string | null, owner?: string): void {
  const s = op.store as MirrorStoreName;
  emitLocalChange(op.uid, {
    store: s,
    operation: crc === null ? "delete" : "set",
    key: op.key,
    crc32c: crc ?? "",
    ...(s === "records" ? { owner: owner ?? "" } : {}),
  });
}

function resolved(op: OutboxOp, resolution: "remote" | "local" | "merged", crc: string | null, owner: string | undefined): void {
  ctx!.emit({ type: "conflict", store: op.store, key: op.key, resolution });
  change(op, crc, owner);
  counts.conflicts++;
}

// drop gives up on op: it leaves the outbox, a corrective change restates
// what the mirror held (unknown, or no entry: none — except a create-only op
// of a key the device knew absent, which restates the delete; an unseen
// create restates nothing, as the server may hold a value), and `failed`
// fires. The mirror entry is invalidated only when op was sent before (an
// earlier attempt may have landed); otherwise the server is unchanged and it
// stays — and it is left alone after a purge since the drop began.
async function drop(op: OutboxOp, code: string): Promise<void> {
  const c = ctx!;
  const ep = c.epoch();
  const s = op.store as MirrorStoreName;
  const p = part(op);
  const got = p ? await c.mirror.get(s, p, op.key).catch(() => null) : null;
  const e = got && got.state !== "unknown" ? got : null;
  await forget([op]);
  if (e && e.state === "present") change(op, e.crc32c, e.owner);
  else if (e || (op.base === "" && !op.unseen)) change(op, null);
  if (p && op.attempts > 0 && c.epoch() === ep) await c.mirror.invalidate(s, p, op.key).catch(() => {});
  c.emit({ type: "failed", store: op.store, key: op.key, code: code as BacklitErrorCode });
  counts.failed++;
}

async function remoteWins(k: Conflict, group: OutboxOp[]): Promise<void> {
  const r = k.remote;
  const crc = r ? r.crc32c : null;
  await commit(k.op, crc ?? "", r && (await r.value.arrayBuffer()), r?.contentType ?? "", r?.owner ?? "", k.uid, group, k);
  resolved(k.op, "remote", crc, r?.owner);
}

const io: ConflictIO = {
  cas: send,
  async settle(op, r, bytes, ct, group, resolution) {
    if (!ok(r.status) && (bytes || r.status !== 404 || r.code === "glow_not_found")) return handle(op, r);
    await commit(op, r.crc32c, bytes, ct, r.owner, r.uid, group, r);
    resolved(op, resolution, bytes ? r.crc32c : null, r.owner);
    return true;
  },
  remoteWins,
};

// --- test hooks -------------------------------------------------------------------

// _setDrainSessionForTest replaces the heartbeat probe (null restores it).
export function _setDrainSessionForTest(p: (() => Promise<Session>) | null): void {
  probe = p ?? sendHeartbeatWithUid;
}

// _setPassBeatTimeoutForTest replaces a pass's heartbeat bound (null: 10 s).
export function _setPassBeatTimeoutForTest(ms: number | null): void {
  beatMs = ms ?? BEAT_MS;
}

// _drainForTest is the running drain, if any.
export const _drainForTest = (): Promise<OfflineSyncResult> | null => running;

// _armingForTest resolves once the latest retry-timer arming has settled.
export const _armingForTest = (): Promise<void> => arming;

// _setRetryTimerForTest replaces the retry timer's set / clear / jitter
// source (null restores the real ones).
export function _setRetryTimerForTest(t: Partial<TimerFns> | null): void {
  disarm();
  timers = t ? { ...realTimers, ...t } : realTimers;
}

// _syncStateForTest is the drain's timing state.
export function _syncStateForTest() {
  return { retryAt, pauseUntil, backoffN, timerN, lastPassAt, held401, armed: timer !== null };
}

export function _resetSyncForTest(): void {
  stopSync();
  ctx = running = null;
  again = held401 = false;
  againBeat = undefined;
  clean = true;
  pauseUntil = retryAt = quietUntil = backoffN = synced = lastPassAt = timerN = 0;
  timers = realTimers;
  arming = Promise.resolve();
  probe = sendHeartbeatWithUid;
  beatMs = BEAT_MS;
}
